import {Routes, Route, Navigate, useLocation, useParams} from 'react-router'
// SaaS-only pages are imported through `@/features/*` barrels so the trial build
// can swap them for teasers via resolve.alias (see vite.edition.ts).
import {ChangeManagement, CMDetails} from '@/features/changeManagement';
import {Billing, Plans} from '@/features/billing';
import {MSPCustomerDetails, MSPCustomers, MSPDashboard} from '@/features/msp';
import {ActivityLogPage} from '@/features/activityLog';
import {MyVpnDevices, VpnConnect, VpnConnections, VpnLayout, VpnPolicies, VpnSessions, VpnSettings, VpnSites} from '@/features/vpn';
import {VpnLookup, VPN_FLOWS} from '@/features/vpnFlows';
import {useIsMsp} from '@/hooks/useIsMsp';
import {useRollout, ROLLOUTS} from '@/hooks/useRollout.ts';
import Home from "../pages/Home.tsx";
import SignIn from "../pages/SignIn.tsx";
import MagicLinkLanding from "../pages/MagicLinkLanding.tsx";
import React from "react";
import AuthOutlet from "./AuthOutlet.tsx";
import ConsoleHandoff from "../pages/ConsoleHandoff.tsx";
import AccountVerifySuccess from "../pages/signup/AuthVerifySuccess.tsx";
import AccountVerifyFail from "../pages/signup/AuthVerifyFail.tsx";
import AccountSignUp from "../pages/signup/AccountSignUp.tsx";
import SignOut from "../pages/SignOut.tsx";
import MustVerify from "../pages/signup/MustVerify.tsx";
import VerifyEmailLanding from "../pages/signup/VerifyEmailLanding.tsx";
import ForgotPassword from "../pages/signup/ForgotPassword.tsx";
import ResetPassword from '../pages/signup/ResetPassword.tsx';
import CreateOrganization from "../pages/onboarding/CreateOrganization.tsx";
import OnboardedOutlet from "./OnboardedOutlet.tsx";
import PermissionOutlet from "./PermissionOutlet.tsx";
import Gateways from '../pages/main/Gateways.tsx';
import Alerts from "../pages/main/Alerts.tsx";
import AlertDetails from "../pages/main/AlertDetails.tsx";
import Views from "../pages/main/Views.tsx";
import PathCheck from "../pages/main/PathCheck.tsx";
import Segmentation from "../pages/main/Segmentation.tsx";
import Segments from "../pages/main/Segments.tsx";
import Credentials from '../pages/main/Credentials.tsx';
import Controllers from '../pages/main/Controllers.tsx';
import ControllerDetails from '../pages/main/ControllerDetails.tsx';
import DeviceDetails from '../pages/main/DeviceDetails.tsx';
import Regions from '../pages/main/Regions.tsx';
import RegionDetails from '../pages/main/RegionDetails.tsx';
import Divisions from '../pages/main/Divisions.tsx';
import Incidents from '../pages/main/Incidents.tsx';
import IncidentDetails from '../pages/main/IncidentDetails.tsx';
import OrganizationSettings from '../pages/settings/OrganizationSettings.tsx';
import AccountLayout from '../pages/account/AccountLayout.tsx';
import ProfileSettings from '../pages/account/ProfileSettings.tsx';
import EmailAddresses from '../pages/account/EmailAddresses.tsx';
import SecuritySettings from '../pages/account/SecuritySettings.tsx';
import AssistantsSettings from '@/pages/account/AssistantsSettings';
import NotificationsLayout from '../pages/notifications/NotificationsLayout.tsx';
import Inbox from '../pages/notifications/Inbox.tsx';
import {DeliveryLogPage, DestinationsPage, RulesPage} from '../pages/notifications/NotificationPages.tsx';
import {
  AiAssistantsPage,
  ChangeManagementPage,
  MaintenanceWindowsPage,
  NetworkDefaultsPage,
  OrgDestinationsPage,
  OrgRoutingPage,
  PsaPage,
} from '../pages/settings/SettingsPages.tsx';
import VerifyNotificationDestination from '../pages/VerifyNotificationDestination.tsx';
import TurnOffNotificationRule from '../pages/TurnOffNotificationRule.tsx';
import VerifyIdentityEmail from '../pages/VerifyIdentityEmail.tsx';
import AdminLayout from '../pages/admin/AdminLayout.tsx';
import Users from '../pages/admin/Users.tsx';
import Contexts from '../pages/admin/Contexts.tsx';
import Teams from '../pages/admin/Teams.tsx';
import Roles from '../pages/admin/Roles.tsx';
import PermissionsOverview from '../pages/admin/PermissionsOverview.tsx';
import AuditCatalog from '../pages/admin/AuditCatalog.tsx';
import TagDefinitions from '../pages/main/TagDefinitions.tsx';
import TransportDefinitions from '../pages/main/TransportDefinitions.tsx';
import AcceptInvitation from '../pages/AcceptInvitation.tsx';
import NetworkPlans from '../pages/main/NetworkPlans.tsx';
import NetworkPlanDetails from '../pages/main/NetworkPlanDetails.tsx';
import EndpointDetails from '../pages/main/EndpointDetails.tsx';
import MyNetwork from '../pages/main/MyNetwork.tsx';
import OAuthConsent from '../pages/OAuthConsent.tsx';

/**
 * Carries the division id across the customer-detail move to the top level.
 *
 * `<Navigate>` can't interpolate a path param, so a bookmark of the old
 * `/admin/msp/customers/:divisionId` needs this to land on the same customer at
 * its new address rather than on the list.
 */
function LegacyCustomerDetailRedirect() {
  const { divisionId } = useParams<{ divisionId: string }>();
  return <Navigate to={`/customers/${divisionId ?? ''}`} replace />;
}

/**
 * Signup invitation emails (pre-July-2026) and the admin console's Copy Link
 * button both minted `/signup?token=…` links; the page lives at
 * /account/signup. Those links are in prospects' inboxes and can't be
 * recalled, so this alias must outlive both producers. `<Navigate>` alone
 * would drop the query string — carry it across explicitly.
 */
function LegacySignupRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/account/signup${search}`} replace />;
}

/**
 * The old address of a page that moved to `to`. Carries the query string and hash
 * across, which a bare `<Navigate to>` drops: `/vpn/sites?region=…` has to land on
 * the same region's sites at the new address.
 */
function Moved({ to }: { to: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
}

const RoutesComponent: React.FC = () => {
  // MSP routes are registered only for orgs in MSP mode. Unlike the other
  // feature gates here, there is no teaser to fall back to: MSP mode is a
  // self-service toggle in Organization Admin → General, not a plan upsell.
  const isMsp = useIsMsp();
  // Beta: the route stays registered so a deep link from an organization outside
  // the rollout lands on /home rather than a blank page.
  const segmentationMatrix = useRollout(ROLLOUTS.segmentationMatrix);
  const psaTicketing = useRollout(ROLLOUTS.psaTicketing);

  return (
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signout" element={<SignOut />} />
        <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
        <Route path="/account/verify-success" element={<AccountVerifySuccess />} />
        <Route path="/account/verify-fail" element={<AccountVerifyFail />} />
        <Route path="/account/signup" element={<AccountSignUp />} />
        <Route path="/signup" element={<LegacySignupRedirect />} />
        {/* The address the magic-link mailer has always used. */}
        <Route path="/auth/magic-link/:token" element={<MagicLinkLanding />} />
        <Route path="/account/forgot" element={<ForgotPassword />} />
        <Route path="/account/reset" element={<ResetPassword />} />
        <Route path="/account/mustverify" element={<MustVerify />} />
        <Route path="/account/verify/:verificationCode" element={<VerifyEmailLanding />} />
        {/* OAuth consent gates auth in-component so it can bounce to
            /signin?returnTo=… and preserve the OAuth query string across login. */}
        <Route path="/oauth/consent" element={<OAuthConsent />} />
        {/* Same reason as OAuth consent, and the same in-component gate: the challenge lives
            in the query string, and AuthOutlet's redirect would drop it. */}
        <Route path="/console-handoff" element={<ConsoleHandoff />} />
        {/* The "turn off this rule" link in a notification email. Public: the signed link
            is the credential, and the page asks before it turns anything off. */}
        <Route path="/notifications/turn-off" element={<TurnOffNotificationRule />} />
        <Route element={<AuthOutlet fallbackPath='/signin' />}>
          <Route path="/onboarding/create-organization" element={<CreateOrganization />} />
          {/* The link in a destination confirmation email. Authenticated on purpose:
              the verify endpoint checks ownership, so an anonymous landing could only
              ever fail. */}
          <Route path="/notifications/verify-destination" element={<VerifyNotificationDestination />} />
          <Route path="/account/verify-email" element={<VerifyIdentityEmail />} />
          <Route element={<OnboardedOutlet />}>
            <Route path="/home" element={<Home />} />
            <Route path="/account" element={<AccountLayout />}>
              {/* `/account` alone is not a page. Redirecting rather than picking a
                  default child keeps the nav's exact-match highlighting honest. */}
              <Route index element={<Navigate to="/account/profile" replace />} />
              <Route path="profile" element={<ProfileSettings />} />
              <Route path="emails" element={<EmailAddresses />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="assistants" element={<AssistantsSettings />} />
              <Route path="vpn" element={<MyVpnDevices />} />
              {/* Notifications moved out to the top level. These stay as redirects so
                  the links in already-sent verification emails keep working. */}
              <Route path="notifications" element={<Navigate to="/notifications/rules" replace />} />
              <Route
                path="notifications/destinations"
                element={<Navigate to="/notifications/destinations" replace />}
              />
              <Route
                path="notifications/activity"
                element={<Navigate to="/notifications/deliveries" replace />}
              />
            </Route>

            <Route path="/notifications" element={<NotificationsLayout />}>
              <Route index element={<Inbox />} />
              <Route path="rules" element={<RulesPage />} />
              <Route path="destinations" element={<DestinationsPage />} />
              <Route path="deliveries" element={<DeliveryLogPage />} />
              {/* The organization's pages moved to Organization Admin. */}
              <Route path="routing" element={<Moved to="/admin/notifications/routing" />} />
              <Route path="routing/destinations" element={<Moved to="/admin/notifications/destinations" />} />
              <Route path="routing/psa" element={<Moved to="/admin/notifications/psa" />} />
            </Route>
            <Route path="/admin" element={<AdminLayout />}>
              <Route element={<PermissionOutlet permission="user:read" fallbackPath="/" />}>
                <Route path="users" element={<Users />} />
              </Route>
              <Route element={<PermissionOutlet permission="context:read" fallbackPath="/" />}>
                <Route path="contexts" element={<Contexts />} />
              </Route>
              <Route element={<PermissionOutlet permission="team:read" fallbackPath="/" />}>
                <Route path="teams" element={<Teams />} />
              </Route>
              <Route element={<PermissionOutlet permission="role:read" fallbackPath="/" />}>
                <Route path="roles" element={<Roles />} />
                <Route path="permissions" element={<PermissionsOverview />} />
              </Route>
              <Route element={<PermissionOutlet permission="org:read" fallbackPath="/" />}>
                <Route path="general" element={<OrganizationSettings />} />
                <Route path="network-defaults" element={<NetworkDefaultsPage />} />
                <Route path="change-management" element={<ChangeManagementPage />} />
              </Route>
              {/* The one settings page, before it was split by topic. Onboarding,
                  the docs and old bookmarks know this address. */}
              <Route path="settings" element={<Moved to="/admin/general" />} />
              <Route element={<PermissionOutlet permission="division:read" fallbackPath="/" />}>
                <Route path="divisions" element={<Divisions />} />
              </Route>
              {/* The MSP surfaces moved to the top level under `/customers` (F8).
                  These paths stay registered so old bookmarks and deep links keep
                  working — there is no catch-all in this tree, so a dropped <Route>
                  would render a blank page rather than a redirect. Unguarded on
                  purpose: the destination enforces both the permission and the
                  `isMsp` check, so a non-MSP caller redirected here bounces on to
                  /home from there. */}
              <Route path="msp/customers" element={<Navigate to="/customers" replace />} />
              <Route path="msp/customers/:divisionId" element={<LegacyCustomerDetailRedirect />} />
              <Route path="msp/dashboard" element={<Navigate to="/customers/dashboard" replace />} />
              <Route element={<PermissionOutlet permission="region:read" fallbackPath="/" />}>
                <Route path="regions" element={<Regions />} />
                <Route path="regions/:id" element={<RegionDetails />} />
              </Route>
              <Route element={<PermissionOutlet permission="billing:read" fallbackPath="/" />}>
                <Route path="billing" element={<Billing />} />
                <Route path="plans" element={<Plans />} />
              </Route>
              <Route element={<PermissionOutlet permission="tag_definition:read" fallbackPath="/" />}>
                <Route path="tags" element={<TagDefinitions />} />
              </Route>
              <Route element={<PermissionOutlet permission="alert_definition:read" fallbackPath="/" />}>
                <Route path="audit-catalog" element={<AuditCatalog />} />
                <Route path="maintenance-windows" element={<MaintenanceWindowsPage />} />
              </Route>
              <Route element={<PermissionOutlet permission="context:manage_teams" fallbackPath="/" />}>
                <Route path="ai-assistants" element={<AiAssistantsPage />} />
              </Route>
              {/* Unguarded, as they were under /notifications: the routing section
                  gates itself on the entitlement and the permission both. */}
              <Route path="notifications/routing" element={<OrgRoutingPage />} />
              <Route path="notifications/destinations" element={<OrgDestinationsPage />} />
              <Route
                path="notifications/psa"
                element={psaTicketing ? <PsaPage /> : <Navigate to="/admin/notifications/routing" replace />}
              />
              <Route element={<PermissionOutlet permission="vpn:read" fallbackPath="/vpn/sessions" />}>
                <Route path="vpn/sites" element={<VpnSites />} />
                <Route path="vpn/policies" element={<VpnPolicies />} />
                <Route path="vpn/settings" element={<VpnSettings />} />
              </Route>
              {/* Not "audit-catalog"'s neighbour by accident — the two are easy to
                  confuse. That one lists *device* audits; this is the user-action log. */}
              <Route element={<PermissionOutlet permission="activity_log:read" fallbackPath="/" />}>
                <Route path="activity-log" element={<ActivityLogPage />} />
              </Route>
              <Route index element={<Users />} />
            </Route>
            {/* Customers — a primary surface for an MSP, promoted out of the
                admin tree (F8). Gated on `msp_customer:read`, never
                `division:read`: managing a customer book is a commercial act, and
                an infrastructure role holding `division:read` has no business
                there. The `isMsp` guard is live, so a redirect covers the org
                toggling MSP mode off while someone is on the page. */}
            <Route element={<PermissionOutlet permission="msp_customer:read" fallbackPath="/home" />}>
              <Route path="/customers" element={isMsp ? <MSPCustomers /> : <Navigate to="/home" replace />} />
              <Route path="/customers/dashboard" element={isMsp ? <MSPDashboard /> : <Navigate to="/home" replace />} />
              <Route path="/customers/:divisionId" element={isMsp ? <MSPCustomerDetails /> : <Navigate to="/home" replace />} />
            </Route>
            <Route path="/network-plans" element={<NetworkPlans />} />
            <Route path="/network-plans/:id" element={<NetworkPlanDetails />} />
            <Route path="/gateways" element={<Gateways />} />
            {/* The technician VPN (docs/VpnAccess.md, "Slice 6 design"). Sessions are
                everyone's page — their own sessions at least; the rest is gated as
                its API is. */}
            {/* The control page the Witina app opens with its link (docs/VpnAccess.md,
                "Slice 9 design"). Signed in like any page: the sign-in keeps the link. */}
            <Route path="/vpn/connect/:code" element={<VpnConnect />} />
            <Route path="/vpn" element={<VpnLayout />}>
              <Route index element={<Navigate to="/vpn/sessions" replace />} />
              {/* The apps connected as you, with their sites' toggles. */}
              <Route path="connect" element={<VpnConnections />} />
              <Route path="sessions" element={<VpnSessions />} />
              {VPN_FLOWS ? (
                <Route element={<PermissionOutlet permission="vpn_session:read" fallbackPath="/vpn/sessions" />}>
                  <Route path="lookup" element={<VpnLookup />} />
                </Route>
              ) : null}
              {/* The organization's pages moved to Organization Admin. */}
              <Route path="sites" element={<Moved to="/admin/vpn/sites" />} />
              <Route path="policies" element={<Moved to="/admin/vpn/policies" />} />
              <Route path="settings" element={<Moved to="/admin/vpn/settings" />} />
            </Route>
            <Route path="/network" element={<MyNetwork />} />
            {/* The old /devices and /endpoints list pages are folded into
                /network (equipment tab = management surface, and the default);
                only the detail routes remain. Redirects keep old deep links
                alive — /endpoints names its tab explicitly since bare /network
                now lands on equipment. */}
            <Route path="/devices" element={<Navigate to="/network?tab=equipment" replace />} />
            <Route path="/devices/:id" element={<DeviceDetails />} />
            <Route path="/endpoints" element={<Navigate to="/network?tab=computers" replace />} />
            <Route path="/endpoints/:id" element={<EndpointDetails />} />
            <Route path="/credentials" element={<Credentials />} />
            <Route path="/controllers" element={<Controllers />} />
            <Route path="/controllers/:id" element={<ControllerDetails />} />
            <Route path="/transport-definitions" element={<TransportDefinitions />} />
            <Route path="/incidents" element={<Incidents />} />
            <Route path="/incidents/:id" element={<IncidentDetails />} />
            <Route path="/alerts" element={<Alerts />} />
            <Route path="/alerts/:id" element={<AlertDetails />} />
            <Route path="/cm" element={<ChangeManagement />} />
            <Route path="/change-management" element={<ChangeManagement />} />
            <Route path="/change-management/:id" element={<CMDetails />} />
            <Route path="/views" element={<Views />} />
            {/* S0 skeleton (dev only): DEV is statically false in built bundles. */}
            {import.meta.env.DEV && <Route path="/path-check" element={<PathCheck />} />}
            <Route path="/segmentation" element={segmentationMatrix ? <Segmentation /> : <Navigate to="/home" replace />} />
            <Route path="/segmentation/:regionId" element={segmentationMatrix ? <Segmentation /> : <Navigate to="/home" replace />} />
            <Route path="/segments" element={<Segments />} />
            <Route path="/segments/:regionId" element={<Segments />} />
            <Route path="/" element={<Home />} />
          </Route>
        </Route>
      </Routes>
  )
}

export default RoutesComponent
